// apps/admin/src/features/billing/TransferGroupMembersDialog.tsx
//
// Phase 8.2 Wave 3 Task 21 — extracted Members + History dialog for a
// single Transfer Group, reused by TransferGroupsPage.tsx.
//
// Per CONTEXT.md / API-CONTRACTS.md §Finance — Transfer Groups:
//   - Whitelist + per-member transfer limits.
//   - Per-member transfer-block flag. The block itself is owned
//                 by 08.1 (MerchantTransferBlock entity + Block from Transfer
//                 Group modal on the merchant detail page); here we surface
//                 the flag read-only on each member row so an admin browsing
//                 the group sees which members are currently blocked from
//                 receiving transfers. Block toggle stays in 08.1.
//
// Endpoints used:
//   GET    /api/v1/admin/transfer-groups/:id/members
//   POST   /api/v1/admin/transfer-groups/:id/members
//   DELETE /api/v1/admin/transfer-groups/:id/members/:merchant_id
//   GET    /api/v1/admin/transfer-groups/:id/history?...&export=csv
//
// Test IDs (from PLAN/task-21.md <test_ids>):
//   - transfer-group-members-dialog          — modal root
//   - transfer-group-member-search-input     — merchant search input
//
// vg-binding: PLAN:task-21
// vg-binding: P8.D-89
// vg-binding: P8.D-458
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MerchantPicker,
  cn,
  useToast,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

// ---------------------------------------------------------------------------
// Wire types — match TransferGroupsPage.tsx serializer shape.
// ---------------------------------------------------------------------------

export interface TransferGroupSummary {
  id: string;
  name: string;
  member_count: number;
  is_active: boolean;
}

export interface TransferGroupMember {
  group_id?: string;
  merchant_id: string;
  merchant_name?: string;
  /** dbg-20260629-tgmembers — owner email surfaced by the BE list endpoint
   *  so the admin can identify the merchant beyond a raw ObjectId. */
  owner_email?: string | null;
  /** dbg-20260629-tgmembers2 — false ⇒ member has no active wallet, so a
   *  transfer to them gets filtered out at send time. Drives a warning badge. */
  can_receive?: boolean;
  max_per_transfer: number | null;
  max_count_per_month: number | null;
  unlimited: boolean;
  added_at?: string;
  /**
   * Whether this merchant currently has an active block-from-transfer
   * record (read-only here; toggle lives in 08.1 Merchant Detail). Optional
   * because the BE join is owned by 08.1 follow-up and may not yet be wired.
   */
  transfer_blocked?: boolean;
  /** Optional reason surfaced as a tooltip when transfer_blocked. */
  transfer_block_reason?: string | null;
}

export interface HistoryRow {
  // dbg-20260629-txhistory — field names MUST match the BE history endpoint
  // response (getHistoryHandler): tx_group_id + recipient_merchant_id.
  // The prior `transfer_id` / `receiver_merchant_id` were never populated →
  // `row.receiver_merchant_id.slice()` threw on the History tab.
  tx_group_id: string;
  sender_merchant_id: string;
  recipient_merchant_id: string;
  /** Owner emails resolved BE-side (dbg-20260723) — shown instead of ObjectIds. */
  sender_email?: string | null;
  recipient_email?: string | null;
  amount_usd_cents: number;
  note: string | null;
  created_at: string;
}

interface ListResponse<T> {
  data: T[];
  next_cursor: string | null;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}
interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

interface ApiError {
  code?: string;
  message: string;
  status: number;
}

function envelopeError(payload: unknown, status: number, t: (key: string) => string): ApiError {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    const err = (payload as ErrorEnvelope).error;
    return { code: err?.code, message: err?.message ?? t('transfer_group.toast.unknownError'), status };
  }
  return { message: t('transfer_group.toast.networkErrorDesc'), status };
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function idemKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `tg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface TransferGroupMembersDialogProps {
  group: TransferGroupSummary | null;
  onClose: () => void;
}

export function TransferGroupMembersDialog({
  group,
  onClose,
}: TransferGroupMembersDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const readOnly = !!group && !group.is_active;

  const [members, setMembers] = useState<TransferGroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Add-member form state.
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [merchantLabel, setMerchantLabel] = useState<string | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  const [maxPerTransfer, setMaxPerTransfer] = useState('');
  const [maxCountPerMonth, setMaxCountPerMonth] = useState('');
  const [adding, setAdding] = useState(false);
  const [merchantError, setMerchantError] = useState<string | null>(null);

  // History tab state — numbered pagination (newest-first BE sort).
  const [activeTab, setActiveTab] = useState<'members' | 'history'>('members');
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState(10);
  const [histTotal, setHistTotal] = useState(0);

  const refreshMembers = useCallback(async (): Promise<void> => {
    if (!group) return;
    setLoadingMembers(true);
    try {
      const client = apiClient();
      const path = `/api/v1/admin/transfer-groups/${encodeURIComponent(group.id)}/members?limit=200`;
      const { data, error, response } = await (
        client as unknown as {
          GET: (p: string) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).GET(path);
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        toast({
          title: t('transfer_group.members.loadFailed'),
          description: env.message,
          variant: 'error',
        });
        setMembers([]);
        return;
      }
      // Defensive: handler may return ListResponse<Member> OR a bare Member[]
      // (the legacy list endpoint serialises the embedded array directly).
      const payload = unwrap<ListResponse<TransferGroupMember> | TransferGroupMember[]>(data);
      const arr = Array.isArray(payload) ? payload : (payload.data ?? []);
      setMembers(arr);
    } catch (err) {
      toast({
        title: t('transfer_group.toast.networkError'),
        description: err instanceof Error ? err.message : t('transfer_group.toast.checkConnection'),
        variant: 'error',
      });
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, [group, toast, t]);

  const refreshHistory = useCallback(async (): Promise<void> => {
    if (!group) return;
    setLoadingHistory(true);
    try {
      const client = apiClient();
      const path = `/api/v1/admin/transfer-groups/${encodeURIComponent(group.id)}/history?page=${histPage}&page_size=${histPageSize}`;
      const { data, error, response } = await (
        client as unknown as {
          GET: (p: string) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).GET(path);
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        toast({
          title: t('transfer_group.members.historyLoadFailed'),
          description: env.message,
          variant: 'error',
        });
        setHistory([]);
        return;
      }
      // Success envelope: { success, data: HistoryRow[], total, page, ... }.
      // `unwrap` returns the array; `total` is a sibling on the envelope.
      const payload = unwrap<ListResponse<HistoryRow> | HistoryRow[]>(data);
      const arr = Array.isArray(payload) ? payload : (payload.data ?? []);
      setHistory(arr);
      const total = (data as { total?: number } | undefined)?.total;
      setHistTotal(typeof total === 'number' ? total : arr.length);
    } catch (err) {
      toast({
        title: t('transfer_group.toast.networkError'),
        description: err instanceof Error ? err.message : t('transfer_group.toast.checkConnection'),
        variant: 'error',
      });
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [group, toast, t, histPage, histPageSize]);

  useEffect(() => {
    if (!group) return;
    setMerchantId(null);
    setMerchantLabel(null);
    setUnlimited(false);
    setMaxPerTransfer('');
    setMaxCountPerMonth('');
    setMerchantError(null);
    setActiveTab('members');
    setHistPage(1);
    setHistTotal(0);
    void refreshMembers();
  }, [group, refreshMembers]);

  useEffect(() => {
    if (group && activeTab === 'history') void refreshHistory();
  }, [activeTab, group, refreshHistory]);

  // useMemo BEFORE the early-null return so the number of hooks per render
  // stays stable (React error #310 otherwise).
  const csvUrl = useMemo(
    () =>
      group
        ? `/api/v1/admin/transfer-groups/${encodeURIComponent(group.id)}/history?export=csv`
        : null,
    [group],
  );

  if (!group) return null;

  async function handleAddMember(): Promise<void> {
    if (!group || readOnly) return;
    const id = (merchantId ?? '').trim();
    if (!/^[a-f0-9]{24}$/.test(id)) {
      setMerchantError(t('transfer_group.members.pickMerchant'));
      return;
    }
    setMerchantError(null);
    setAdding(true);
    try {
      const client = apiClient();
      const path = `/api/v1/admin/transfer-groups/${encodeURIComponent(group.id)}/members`;
      const body: Record<string, unknown> = { merchant_id: id, unlimited };
      if (!unlimited) {
        if (maxPerTransfer) body.max_per_transfer = Number(maxPerTransfer);
        if (maxCountPerMonth) body.max_count_per_month = Number(maxCountPerMonth);
      }
      const { error, response } = await (
        client as unknown as {
          POST: (
            p: string,
            init: { body: unknown; headers?: Record<string, string> },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).POST(path, { body, headers: { 'Idempotency-Key': idemKey() } });
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        if (
          env.code === 'MEMBER_ALREADY_IN_GROUP' ||
          env.code === 'ALREADY_MEMBER' ||
          env.code === 'MERCHANT_ALREADY_MEMBER' ||
          response.status === 409
        ) {
          setMerchantError(t('transfer_group.members.alreadyMember'));
          return;
        }
        setMerchantError(env.message);
        return;
      }
      toast({ title: t('transfer_group.members.added'), variant: 'success' });
      setMerchantId(null);
      setMerchantLabel(null);
      setMaxPerTransfer('');
      setMaxCountPerMonth('');
      setUnlimited(false);
      await refreshMembers();
    } catch (err) {
      toast({
        title: t('transfer_group.toast.networkError'),
        description: err instanceof Error ? err.message : t('transfer_group.toast.checkConnection'),
        variant: 'error',
      });
    } finally {
      setAdding(false);
    }
  }

  async function handleRemoveMember(member: TransferGroupMember): Promise<void> {
    if (!group || readOnly) return;
    try {
      const client = apiClient();
      const path = `/api/v1/admin/transfer-groups/${encodeURIComponent(group.id)}/members/${encodeURIComponent(member.merchant_id)}`;
      const { error, response } = await (
        client as unknown as {
          DELETE: (
            p: string,
            init?: { headers?: Record<string, string> },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).DELETE(path, { headers: { 'Idempotency-Key': idemKey() } });
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        toast({
          title: t('transfer_group.members.removeFailed'),
          description: env.message,
          variant: 'error',
        });
        return;
      }
      toast({ title: t('transfer_group.members.removed'), variant: 'success' });
      await refreshMembers();
    } catch (err) {
      toast({
        title: t('transfer_group.toast.networkError'),
        description: err instanceof Error ? err.message : t('transfer_group.toast.checkConnection'),
        variant: 'error',
      });
    }
  }

  function txHistoryHref(): string {
    // Cross-link to the Transactions page filtered by this group id. The admin
    // route is /admin/billing/transactions (App.tsx) — the /billing/... path
    // 404s (dbg-20260723).
    return `/admin/billing/transactions?group=${encodeURIComponent(group!.id)}`;
  }

  return (
    <Dialog
      open={!!group}
      onOpenChange={(o) => !o && onClose()}
      // dbg-20260629-tgmembers2 — widen past the default max-w-lg so the
      // members list (name + email + 3 limit columns + actions) doesn't
      // truncate the email. max-h + overflow keeps tall member lists scrollable.
      className="max-w-3xl max-h-[85vh] overflow-y-auto"
    >
      <div data-testid="transfer-group-members-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('transfer_group.members.title', { name: group.name })}
            {readOnly ? (
              <span
                className="ml-2 inline-flex h-5 items-center rounded-md bg-pw-surface px-2 text-[10px] font-semibold uppercase tracking-wider text-pw-text-muted"
                data-testid="transfer-group-readonly-badge"
              >
                {t('transfer_group.members.readonlyBadge')}
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        {/* Tab nav */}
        <div className="mt-4 flex gap-2 border-b border-pw-border" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'members'}
            onClick={() => setActiveTab('members')}
            className={cn(
              '-mb-px h-9 border-b-2 px-3 text-sm font-medium',
              activeTab === 'members'
                ? 'border-pw-primary text-pw-primary'
                : 'border-transparent text-pw-text-muted hover:text-pw-text',
            )}
          >
            {t('transfer_group.members.membersTab', { count: group.member_count })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'history'}
            onClick={() => setActiveTab('history')}
            className={cn(
              '-mb-px h-9 border-b-2 px-3 text-sm font-medium',
              activeTab === 'history'
                ? 'border-pw-primary text-pw-primary'
                : 'border-transparent text-pw-text-muted hover:text-pw-text',
            )}
          >
            {t('transfer_group.members.historyTab')}
          </button>
        </div>

        {activeTab === 'members' ? (
          <div className="mt-4 space-y-4">
            {/* Add-member form */}
            <div className="rounded-lg border border-pw-border bg-pw-surface/30 p-4">
              <h3 className="text-sm font-semibold text-pw-text">{t('transfer_group.members.addMerchant')}</h3>
              {readOnly ? (
                <p className="mt-2 text-xs text-pw-text-muted">
                  {t('transfer_group.members.inactiveHint')}
                </p>
              ) : null}
              <div className="mt-3 space-y-3">
                <div>
                  <label
                    htmlFor="transfer-group-member-search-input"
                    className="mb-1 block text-xs font-medium text-pw-text-muted"
                  >
                    {t('transfer_group.members.searchMerchant')}
                  </label>
                  <MerchantPicker
                    value={merchantId}
                    valueLabel={merchantLabel}
                    onChange={(id, label) => {
                      setMerchantId(id);
                      setMerchantLabel(label);
                      if (merchantError) setMerchantError(null);
                    }}
                    placeholder={t('transfer_group.members.searchPlaceholder')}
                    disabled={readOnly}
                    testId="transfer-group-member-search-input"
                  />
                  {merchantError ? (
                    <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
                      {merchantError}
                    </p>
                  ) : null}
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={unlimited}
                    onChange={(e) => setUnlimited(e.target.checked)}
                    className="h-4 w-4 rounded border-pw-border text-pw-primary focus:ring-pw-primary"
                    disabled={readOnly}
                    data-testid="member-unlimited"
                  />
                  <span className="text-pw-text">{t('transfer_group.members.unlimited')}</span>
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="member-max-per-transfer"
                      className="mb-1 block text-xs font-medium text-pw-text-muted"
                    >
                      {t('transfer_group.members.maxPerTransfer')}
                    </label>
                    <Input
                      id="member-max-per-transfer"
                      type="number"
                      value={maxPerTransfer}
                      onChange={(e) => setMaxPerTransfer(e.target.value)}
                      placeholder="100000"
                      min={1}
                      disabled={unlimited || readOnly}
                      aria-disabled={unlimited || readOnly}
                      data-testid="member-max-per-transfer"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="member-max-count"
                      className="mb-1 block text-xs font-medium text-pw-text-muted"
                    >
                      {t('transfer_group.members.maxCount')}
                    </label>
                    <Input
                      id="member-max-count"
                      type="number"
                      value={maxCountPerMonth}
                      onChange={(e) => setMaxCountPerMonth(e.target.value)}
                      placeholder="50"
                      min={1}
                      disabled={unlimited || readOnly}
                      aria-disabled={unlimited || readOnly}
                      data-testid="member-max-count"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleAddMember}
                  disabled={adding || readOnly}
                  data-testid="transfer-group-add-member-btn"
                >
                  {adding
                    ? t('transfer_group.members.adding')
                    : t('transfer_group.members.addMemberBtn')}
                </Button>
              </div>
            </div>

            {/* Members list */}
            <div className="overflow-hidden rounded-lg border border-pw-border">
              <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr_auto] gap-3 border-b border-pw-border bg-pw-surface/40 px-4 py-2 text-xs font-medium uppercase tracking-wider text-pw-text-muted">
                <div>{t('transfer_group.members.colMerchant')}</div>
                <div>{t('transfer_group.members.colPerTransfer')}</div>
                <div>{t('transfer_group.members.colPerMonth')}</div>
                <div>{t('transfer_group.members.colStatus')}</div>
                <div className="text-right">{t('transfer_group.members.colActions')}</div>
              </div>
              {loadingMembers ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {t('transfer_group.members.loadingMembers')}
                </div>
              ) : members.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {t('transfer_group.members.noMembers')}
                </div>
              ) : (
                <ul className="divide-y divide-pw-border">
                  {members.map((m) => (
                    <li
                      key={m.merchant_id}
                      className="grid grid-cols-[1.6fr_1fr_1fr_1fr_auto] items-center gap-3 px-4 py-3"
                      data-testid={`transfer-group-member-row-${m.merchant_id}`}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-pw-text">
                          {m.merchant_name || m.merchant_id}
                        </p>
                        {/* dbg-20260629-tgmembers — show owner email (who),
                            fall back to the shortened id only when unknown. */}
                        {m.owner_email ? (
                          <p className="truncate text-xs text-pw-text-muted">
                            {m.owner_email}
                          </p>
                        ) : (
                          <p className="font-mono text-xs text-pw-text-muted">
                            {m.merchant_id.slice(0, 6)}…{m.merchant_id.slice(-4)}
                          </p>
                        )}
                      </div>
                      <div className="text-sm text-pw-text">
                        {m.unlimited
                          ? '—'
                          : m.max_per_transfer
                            ? formatUsd(m.max_per_transfer)
                            : '—'}
                      </div>
                      <div className="text-sm text-pw-text">
                        {m.unlimited
                          ? t('transfer_group.members.unlimitedValue')
                          : m.max_count_per_month != null
                            ? t('transfer_group.members.perMonthSuffix', {
                                count: m.max_count_per_month,
                              })
                            : '—'}
                      </div>
                      <div>
                        {m.transfer_blocked ? (
                          <span
                            className="inline-flex h-6 items-center rounded-md bg-rose-100 dark:bg-rose-950/40 px-2 text-[10px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300"
                            title={
                              m.transfer_block_reason
                                ? t('transfer_group.members.blockedReason', {
                                    reason: m.transfer_block_reason,
                                  })
                                : t('transfer_group.members.blockedTooltip')
                            }
                            data-testid={`transfer-group-member-blocked-${m.merchant_id}`}
                          >
                            {t('transfer_group.members.blocked')}
                          </span>
                        ) : m.can_receive === false ? (
                          // dbg-20260629-tgmembers2 — member added but has no
                          // active wallet (e.g. still pending onboarding), so a
                          // transfer to them is filtered at send time. Warn the
                          // admin instead of silently dropping the recipient.
                          <span
                            className="inline-flex h-6 items-center rounded-md bg-amber-100 dark:bg-amber-950/40 px-2 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300"
                            title={t('transfer_group.members.noWalletTooltip', {
                              defaultValue:
                                'This merchant has no active wallet yet — they cannot receive transfers until onboarding completes.',
                            })}
                            data-testid={`transfer-group-member-no-wallet-${m.merchant_id}`}
                          >
                            {t('transfer_group.members.noWallet', {
                              defaultValue: 'No wallet',
                            })}
                          </span>
                        ) : (
                          <span className="text-xs text-pw-text-muted">
                            {t('transfer_group.status.active')}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-600 dark:text-rose-300 hover:bg-rose-50"
                          onClick={() => void handleRemoveMember(m)}
                          disabled={readOnly}
                          data-testid={`transfer-group-member-remove-btn-${m.merchant_id}`}
                        >
                          {t('transfer_group.members.remove')}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-pw-text-muted">
                {t('transfer_group.members.historyBlurb')}
              </p>
              <div className="flex gap-2">
                <a
                  href={txHistoryHref()}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-pw-border bg-pw-bg px-3 text-xs font-semibold text-pw-text hover:bg-pw-surface"
                  data-testid="transfer-group-tx-history-link"
                >
                  {t('transfer_group.members.viewInTransactions')}
                </a>
                <a
                  href={csvUrl ?? undefined}
                  download={`transfer-group-${group.id}-history.csv`}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-pw-border bg-pw-bg px-3 text-xs font-semibold text-pw-text hover:bg-pw-surface"
                  data-testid="history-csv-export"
                >
                  <DownloadIcon /> {t('transfer_group.members.exportCsv')}
                </a>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-pw-border">
              <div className="grid grid-cols-[1.4fr_1.6fr_1.6fr_1fr] gap-3 border-b border-pw-border bg-pw-surface/40 px-4 py-2 text-xs font-medium uppercase tracking-wider text-pw-text-muted">
                <div>{t('transfer_group.members.histDate')}</div>
                <div>{t('transfer_group.members.histSender')}</div>
                <div>{t('transfer_group.members.histReceiver')}</div>
                <div className="text-right">{t('transfer_group.members.histAmount')}</div>
              </div>
              {loadingHistory ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {t('transfer_group.members.loadingHistory')}
                </div>
              ) : history.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {t('transfer_group.members.noTransfers')}
                </div>
              ) : (
                <ul className="divide-y divide-pw-border">
                  {history.map((row) => (
                    <li
                      key={row.tx_group_id}
                      className="grid grid-cols-[1.4fr_1.6fr_1.6fr_1fr] items-center gap-3 px-4 py-3 text-sm"
                    >
                      <div className="text-pw-text-muted">
                        {new Date(row.created_at).toLocaleString()}
                      </div>
                      <div
                        className="truncate text-xs text-pw-text"
                        title={row.sender_email ?? row.sender_merchant_id}
                      >
                        {row.sender_email ?? `${(row.sender_merchant_id ?? '').slice(0, 6)}…`}
                      </div>
                      <div
                        className="truncate text-xs text-pw-text"
                        title={row.recipient_email ?? row.recipient_merchant_id}
                      >
                        {row.recipient_email ?? `${(row.recipient_merchant_id ?? '').slice(0, 6)}…`}
                      </div>
                      <div className="text-right font-semibold text-pw-text">
                        {formatUsd(row.amount_usd_cents)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {histTotal > 0 ? (
                <div className="border-t border-pw-border px-4 py-2">
                  <PageListFooter
                    page={histPage}
                    pageSize={histPageSize}
                    total={histTotal}
                    totalPages={Math.max(1, Math.ceil(histTotal / histPageSize))}
                    loading={loadingHistory}
                    onPageChange={setHistPage}
                    onPageSizeChange={(s) => {
                      setHistPageSize(s);
                      setHistPage(1);
                    }}
                    testId="transfer-group-history-footer"
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('transfer_group.members.close')}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}

function DownloadIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export default TransferGroupMembersDialog;
